const TURNSTILE_SCRIPT_ID = "cloudflare-turnstile-script";
const TURNSTILE_SCRIPT_URL =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const DEVELOPMENT_SITE_KEY = "1x00000000000000000000AA";
const PRODUCTION_SITE_KEY = "0x4AAAAAAEKxZeumzxh1oy-R";

type TurnstileOptions = {
  sitekey: string;
  action: string;
  theme: "auto";
  callback: (token: string) => void;
  "error-callback": (errorCode?: string) => void;
  "expired-callback": () => void;
  "timeout-callback": () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileOptions) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;
let challengePromise: Promise<string> | null = null;

export function requestTurnstileToken(signal?: AbortSignal): Promise<string> {
  const testToken = import.meta.env.VITE_TURNSTILE_TEST_TOKEN;
  if (testToken) return Promise.resolve(testToken);
  if (challengePromise) return challengePromise;

  challengePromise = showChallenge(signal).finally(() => {
    challengePromise = null;
  });
  return challengePromise;
}

async function showChallenge(signal?: AbortSignal): Promise<string> {
  const turnstile = await loadTurnstile();
  if (signal?.aborted) {
    throw signal.reason ?? new DOMException("Aborted", "AbortError");
  }

  return new Promise<string>((resolve, reject) => {
    const dialog = document.createElement("dialog");
    dialog.className = "turnstile-dialog";
    dialog.setAttribute("aria-labelledby", "turnstile-dialog-title");

    const title = document.createElement("h2");
    title.id = "turnstile-dialog-title";
    title.textContent = "Verify you are human";

    const message = document.createElement("p");
    message.textContent = "Complete this quick check to use the sponsored AI service.";

    const widget = document.createElement("div");
    widget.className = "turnstile-dialog__widget";

    const cancel = document.createElement("button");
    cancel.type = "button";
    cancel.textContent = "Cancel";

    dialog.append(title, message, widget, cancel);
    document.body.append(dialog);
    dialog.showModal();

    let widgetId: string | null = null;
    let settled = false;
    const finish = (result: { token: string } | { error: Error }) => {
      if (settled) return;
      settled = true;
      signal?.removeEventListener("abort", handleAbort);
      if (widgetId) turnstile.remove(widgetId);
      dialog.close();
      dialog.remove();
      if ("token" in result) resolve(result.token);
      else reject(result.error);
    };
    const handleAbort = () =>
      finish({
        error:
          signal?.reason instanceof Error
            ? signal.reason
            : new DOMException("Aborted", "AbortError"),
      });
    const failChallenge = (message: string) => finish({ error: new Error(message) });

    cancel.addEventListener("click", handleAbort, { once: true });
    dialog.addEventListener(
      "cancel",
      (event) => {
        event.preventDefault();
        handleAbort();
      },
      { once: true },
    );
    signal?.addEventListener("abort", handleAbort, { once: true });

    widgetId = turnstile.render(widget, {
      sitekey:
        import.meta.env.VITE_TURNSTILE_SITE_KEY ??
        (import.meta.env.DEV ? DEVELOPMENT_SITE_KEY : PRODUCTION_SITE_KEY),
      action: "borrow-api-key",
      theme: "auto",
      callback: (token) => finish({ token }),
      "error-callback": (errorCode) =>
        failChallenge(
          errorCode ? `Human verification failed (${errorCode}).` : "Human verification failed.",
        ),
      "expired-callback": () => failChallenge("Human verification expired."),
      "timeout-callback": () => failChallenge("Human verification timed out."),
    });
  });
}

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<TurnstileApi>((resolve, reject) => {
    const existingScript = document.getElementById(TURNSTILE_SCRIPT_ID) as HTMLScriptElement | null;
    const script = existingScript ?? document.createElement("script");
    const handleLoad = () => {
      if (window.turnstile) resolve(window.turnstile);
      else reject(new Error("The human-verification service did not load."));
    };
    const handleError = () =>
      reject(new Error("The human-verification service could not be loaded."));

    script.addEventListener("load", handleLoad, { once: true });
    script.addEventListener("error", handleError, { once: true });
    if (!existingScript) {
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      document.head.append(script);
    }
  }).catch((error: unknown) => {
    scriptPromise = null;
    throw error;
  });
  return scriptPromise;
}
